import {
  CellClassParams,
  ColDef,
  ValueFormatterParams,
  ValueParserParams,
} from 'ag-grid-community';

import {
  StrippingFertilizationResponse,
  UpdateFertilizationRequest,
} from '@/services/stripping/types';
import { StringMapBase } from '@/types/common';
import { calculateIvcDate, formatDateWithPattern } from '@/utils/formatting';
import { formatTime } from '@/utils/formatting';

import { StrippingDonorCell } from './StrippingDonorCell';
import { Values } from './types';
import { calculateIvc, nullToFalseFormatter } from './utils';

export const getColumnDefs = (mappingData: {
  semenTypeData: StringMapBase[];
  fertilizationDate: string;
}): ColDef<StrippingFertilizationResponse>[] => {
  return [
    {
      field: 'strippedAt',
      headerName: 'IVC Date',
      valueFormatter: ({ data }: { data: StrippingFertilizationResponse }) => {
        if (data.strippedAt === null && data.ivfDate) {
          const ivcDate = calculateIvcDate(data.ivfDate);
          return formatDateWithPattern(ivcDate, 'MM-dd-yyyy');
        } else {
          return data.strippedAt
            ? formatDateWithPattern(new Date(data.strippedAt), 'MM-dd-yyyy')
            : '';
        }
      },
      filter: 'agSetColumnFilter',
      cellEditor: 'agDateCellEditor',
      sort: 'asc',
      sortable: true,
      width: 110,
      comparator: strippedAtComparator,
    },
    {
      field: 'donorSchedule.donor.name',
      headerName: 'Donor',
      width: 230,
      filter: 'agSetColumnFilter',
      filterParams: {
        applyMiniFilterWhileTyping: true,
      },
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) => {
        if (data?.aspirationPool?.donorsInPool) {
          return `${data.aspirationPool.name}: ${data.aspirationPool.donorsInPool}`;
        }
        return data?.donorSchedule?.donor?.name ?? '';
      },
      cellRenderer: StrippingDonorCell,
      cellStyle({
        data,
        context,
      }: {
        data: StrippingFertilizationResponse;
        context: string[] | undefined;
      }) {
        if (!data || !context) {
          return { backgroundColor: '' };
        }

        if (data?.breed?.color) {
          return {
            backgroundColor: `rgb(${data.breed.color}, 0.7)`,
          };
        }

        const isDuplicateDonor = context.includes(
          data?.donorSchedule?.donor?.id as string,
        );
        if (isDuplicateDonor) {
          return {
            backgroundColor: 'rgb(255, 255, 0, 0.7)',
          };
        }

        return { backgroundColor: '' };
      },
    },
    {
      field: 'donorSchedule.sire.name',
      headerName: 'Sire',
      width: 160,
    },
    {
      field: 'semen.colCode',
      headerName: 'Col. Code',
      width: 90,
    },

    {
      field: 'donorSchedule.semenType',
      headerName: 'Semen Type',
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) {
          const semenType = mappingData.semenTypeData?.find(
            (semenType: { key: number }) => semenType.key === params.value,
          );
          return semenType
            ? semenType.value !== null
              ? semenType.value
              : ' '
            : params.value;
        }
      },
      width: 90,
    },
    {
      field: 'qcIncubator',
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.qcIncubator ? (data.qcIncubator?.name as string) : ' ',
      headerName: 'Incubator',
      width: 90,
    },
    {
      id: 'strippedAtTime',
      field: 'strippedAt',
      headerName: 'IVC Time',
      width: 78,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.strippedAt ? formatTime(data.strippedAt) : '',
      valueParser: (params: ValueParserParams) => {
        const value = params.newValue;
        if (value && value.length === 4) {
          const hours = parseInt(value.substring(0, 2));
          const minutes = parseInt(value.substring(2));
          if (
            !Number.isNaN(hours) &&
            !Number.isNaN(minutes) &&
            hours >= 0 &&
            hours <= 24 &&
            minutes >= 0 &&
            minutes < 60
          ) {
            return value;
          }
        }
        return 'INVALID';
      },
    },
    {
      field: 'ivfNumber',
      headerName: 'IVF Number',
      cellDataType: 'number',
      width: 100,
    },
    {
      field: 'ivcNumber',
      headerName: 'IVC Number',

      cellDataType: 'number',
      width: 100,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.ivcNumber !== null ? data.ivcNumber : null,
      cellClassRules: {
        'bg-red-400': (params: CellClassParams) => {
          const { data } = params;
          const valid = data && calculateIvc(data) === data.ivcNumber;
          return data && !valid;
        },
      },
    },

    {
      field: 'ivcBroken',
      headerName: 'Broken',

      cellDataType: 'number',
      width: 90,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.ivcBroken,
    },
    {
      field: 'ivcDead',
      headerName: 'Dead',
      cellDataType: 'number',
      width: 90,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.ivcDead,
    },
    {
      field: 'ivcMissing',
      headerName: 'Missing',
      cellDataType: 'number',
      width: 90,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.ivcMissing,
    },
    {
      field: 'ivcImmature',
      headerName: 'Immature',
      cellDataType: 'number',
      width: 90,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.ivcImmature,
    },
    {
      field: 'ivcExtra',
      headerName: 'Extra',
      cellDataType: 'number',
      width: 90,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        data.ivcExtra,
    },
    {
      headerName: 'Calc IVC',
      width: 90,
      valueGetter: ({ data }: { data: Values }) => calculateIvc(data),
    },
    {
      field: 'hasNoMotileSperm',
      headerName: 'No Motile Sperm',
      cellDataType: 'boolean',
      width: 90,
      valueFormatter: nullToFalseFormatter,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        !!data.hasNoMotileSperm,
    },
    {
      field: 'hasFewMotileSperm',
      headerName: 'Few Motile Sperm',
      cellDataType: 'boolean',
      width: 90,
      valueFormatter: nullToFalseFormatter,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        !!data.hasFewMotileSperm,
    },

    {
      field: 'isHardToStrip',
      headerName: 'Hard to Strip',
      cellDataType: 'boolean',
      width: 90,
      valueFormatter: nullToFalseFormatter,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        !!data.isHardToStrip,
    },
    {
      field: 'isFragile',
      headerName: 'Fragile',
      cellDataType: 'boolean',
      width: 90,
      valueFormatter: nullToFalseFormatter,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        !!data.isFragile,
    },
    {
      field: 'hasTwoCellCleavage',
      headerName: '2 Cell Cleavage',
      cellDataType: 'boolean',
      width: 90,
      valueFormatter: nullToFalseFormatter,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        !!data.hasTwoCellCleavage,
    },
    {
      field: 'hasPoorCytoplasmQuality',
      headerName: 'Poor Cytoplasm Quality',
      cellDataType: 'boolean',
      width: 90,
      valueFormatter: nullToFalseFormatter,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        !!data.hasPoorCytoplasmQuality,
    },
    {
      field: 'contaminationInFert',
      headerName: 'Contamination In Fertilization',
      cellDataType: 'boolean',
      width: 90,
      valueFormatter: nullToFalseFormatter,
      valueGetter: ({ data }: { data: StrippingFertilizationResponse }) =>
        !!data.contaminationInFert,
    },
    {
      field: 'stripperTipLotNumber',
      headerName: 'Stripper Tip Lot Number',
    },
    {
      field: 'ivcComment',
      headerName: 'IVC Comment',
    },
    {
      field: 'ivcTechnician.name',
      headerName: 'IVC Technician',
      width: 150,
    },
  ] as ColDef[];
};

const strippedAtComparator = (valueA: string, valueB: string) => {
  const dateA = valueA ? new Date(valueA) : null;
  const dateB = valueB ? new Date(valueB) : null;

  if (dateA === null && dateB === null) return 0;
  if (dateA === null) return -1;
  if (dateB === null) return 1;

  return 0;
};

export type Row = Omit<
  UpdateFertilizationRequest,
  'strippedAt' | 'ivcTechnicianId'
> & {
  id: string;
  donorId: string;
  donor: string;
  sire: string;
  colCode: string;
  semenType: string;
  ivcNumber: number;
  ivfNumber: number | null;
  ivcTechnician: string;
  strippedAt: string | null;
  breed: { color: string | null };
};
